import { useMemo } from 'react';
import { useJobs } from '@/hooks/useJobs';
import { GradientHeading } from './ui/gradient-heading';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { Alert, AlertDescription } from './ui/alert';
import { AspectRatio } from './ui/aspect-ratio';
import { MapPin, Briefcase, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export const PaechterJobs = () => {
  const { allJobs, isLoading, error } = useJobs();

  const paechterJobs = useMemo(() => {
    return allJobs.filter(job => {
      const title = job.title.toLowerCase();
      const description = (job.description || '').toLowerCase();
      const searchTerms = [
        'pächt',
        'paecht',
        'pacht',
        'pächter',
        'paechter',
        'pachter',
        'verpachtung',
        'pachtvertrag',
        'pachtobjekt'
      ];
      
      // Check if title or description contains any of the search terms
      return searchTerms.some(term => 
        title.includes(term) || description.includes(term)
      );
    });
  }, [allJobs]);

  if (isLoading) {
    return (
      <section className="relative py-16 sm:py-20 lg:py-24 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <GradientHeading level="h2" size="md" align="center" className="mb-12">
            Aktuelle Pächter-Positionen
          </GradientHeading>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <Card key={i} className="p-6 backdrop-blur-xl bg-background/70 border-border/40">
                <Skeleton className="h-6 w-3/4 mb-4" />
                <Skeleton className="h-4 w-1/2 mb-4" />
                <Skeleton className="h-20 mb-4" />
                <Skeleton className="h-10 w-full" />
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="relative py-16 sm:py-20 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Alert variant="destructive" className="max-w-2xl mx-auto">
            <AlertDescription>
              Die Stellenangebote konnten nicht geladen werden. Bitte versuchen Sie es später erneut.
            </AlertDescription>
          </Alert>
        </div>
      </section>
    );
  }

  if (paechterJobs.length === 0) {
    return (
      <section className="relative py-16 sm:py-20 lg:py-24 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <GradientHeading level="h2" size="md" align="center" className="mb-8">
            Aktuelle Pächter-Positionen
          </GradientHeading>
          
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-lg text-muted-foreground mb-8">
              Aktuell sind keine speziellen Pächter-Positionen verfügbar. 
              Entdecken Sie unsere anderen Stellenangebote oder senden Sie uns eine Initiativbewerbung.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild variant="premium" size="lg">
                <Link to="/bewerbende">
                  Alle Stellenangebote anzeigen
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/bewerbende#kontakt">
                  Initiativbewerbung senden
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-16 sm:py-20 lg:py-24 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/3 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <GradientHeading level="h2" size="md" align="center" className="mb-4">
          Aktuelle Pächter-Positionen
        </GradientHeading>
        
        <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto text-lg">
          Entdecken Sie spannende Möglichkeiten in der Gastronomie-Verpachtung
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {paechterJobs.map(job => {
            const shortDescription = job.description
              ? job.description.replace(/<[^>]*>/g, '').substring(0, 150) + '...'
              : 'Weitere Details finden Sie in der vollständigen Stellenbeschreibung.';
            
            // Extract image URL from description if not provided directly
            const extractImageUrl = (desc: string): string | null => {
              const imgMatch = desc.match(/<img[^>]+src=["']([^"']+)["']/i);
              return imgMatch ? imgMatch[1] : null;
            };
            
            const imageUrl = job.imageUrl || extractImageUrl(job.description || '');

            return (
              <Card 
                key={job.id} 
                className="group overflow-hidden backdrop-blur-xl bg-background/70 border-border/40 shadow-[0_8px_30px_rgba(0,0,0,0.12)] hover:shadow-[0_20px_60px_rgba(0,0,0,0.15)] transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1"
              >
                <article>
                  {/* Image Section */}
                  {imageUrl ? (
                    <AspectRatio ratio={16 / 9} className="overflow-hidden">
                      <img 
                        src={imageUrl} 
                        alt={`Pächter-Position: ${job.title} in ${job.location}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </AspectRatio>
                  ) : (
                    <div className="relative">
                      <AspectRatio ratio={16 / 9} className="bg-gradient-to-br from-primary/20 via-primary/10 to-background" />
                      <span className="sr-only">Kein Bild verfügbar für {job.title}</span>
                    </div>
                  )}
                  
                  {/* Content Section */}
                  <div className="p-6">
                    <div className="mb-4">
                      <h3 className="font-display font-medium text-xl mb-3 line-clamp-2 group-hover:text-primary transition-colors">
                        {job.title}
                      </h3>
                      
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <MapPin className="h-4 w-4 flex-shrink-0" />
                        <span>{job.location}</span>
                      </div>
                      
                      {job.employmentType && (
                        <Badge variant="secondary" className="mb-3">
                          <Briefcase className="h-3 w-3 mr-1" />
                          {job.employmentType}
                        </Badge>
                      )}
                      
                      {job.salary && (
                        <p className="text-sm font-medium text-primary mb-3">
                          {job.salary}
                        </p>
                      )}
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-6 line-clamp-3">
                      {shortDescription}
                    </p>
                    
                    <Button 
                      asChild 
                      variant="outline" 
                      className="w-full group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-all"
                    >
                      <Link to={`/stellenangebote/${job.id}`}>
                        Details anzeigen
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </div>
                </article>
              </Card>
            );
          })}
        </div>
        
        <div className="text-center mt-12">
          <Button asChild variant="premium" size="lg">
            <Link to="/bewerbende">
              Alle Stellenangebote anzeigen
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
